// client/src/api/services/adminservice.ts
import { apiClient } from '../client';
import { getErrorMessage } from '@/utils/errorUtils';
import { ENDPOINTS } from '../endpoints';
import { AxiosError } from 'axios';

/** Type-safe helper to extract error message from Axios errors */
const getAdminErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.message || error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
};

// ─── Common API response type ─────────────────────────────────────────────────
export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
}

// ─── Parameter types ──────────────────────────────────────────────────────────

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface UserListParams extends PaginationParams {
  role?: string;
  search?: string;
  q?: string;
}

export interface UserSearchParams extends PaginationParams {
  search: string;
}

export interface UserDeleteParams {
  id: string;
}

export interface UserEditParams {
  id: string;
  formData: Record<string, unknown>;
}

export interface UserFilterParams extends PaginationParams {
  status: string;
}

export interface HotspotListParams extends PaginationParams {}

export interface HotspotSearchParams extends PaginationParams {
  search: string;
}

export interface GraphParams {
  period: string;
}

export interface CountryManagerRequestParams extends PaginationParams {
  status?: string;
  search?: string;
  country?: string;
}

export interface PlanData {
  name?: string;
  description?: string;
  price?: number;
  duration?: number;
  features?: string[];
  [key: string]: unknown;
}

// ─── Service functions ────────────────────────────────────────────────────────

export const DASHBOARD = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.DASHBOARD}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch dashboard cards'));
  }
};

export const USERS = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.USER}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch dashboard cards'));
  }
};

export const GROWTH = async (data: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.GROWTH}/${data}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch dashboard cards'));
  }
};

export const HOTSPOT = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.HOTSPOT}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch dashboard cards'));
  }
};

export const USER_STATISTICS = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.USER_STATISTICS}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching user statistics:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch user statistics'));
  }
};

export const USER_USERS = async (DATA: UserListParams): Promise<ApiResponse> => {
  try {
    const params = new URLSearchParams();
    if (DATA?.page) params.append('page', String(DATA.page));
    if (DATA?.limit) params.append('limit', String(DATA.limit));
    if (DATA?.role) params.append('role', String(DATA.role));
    if (DATA?.search) params.append('search', String(DATA.search));
    if (DATA?.q) params.append('q', String(DATA.q));

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.USER_USERS}${query}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching user list:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch user list'));
  }
};

export const USER_SEARCH = async (DATA: UserSearchParams): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.USER_USERS}?search=${DATA.search}&page=${DATA.page}&limit=${DATA.limit}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching user list:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch user list'));
  }
};

export const USER_DELETE = async (DATA: UserDeleteParams): Promise<ApiResponse> => {
  try {
    const response = await apiClient.delete<ApiResponse>(`${ENDPOINTS.ADMIN.USER_USERS}/${DATA.id}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error deleting user:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to delete user'));
  }
};

export const USER_EDIT = async (DATA: UserEditParams): Promise<ApiResponse> => {
  try {
    const response = await apiClient.put<ApiResponse>(`${ENDPOINTS.ADMIN.USER_USERS}/${DATA.id}`, DATA.formData);
    return response.data;
  } catch (error: unknown) {
    console.error('Error editing user:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to edit user'));
  }
};

export const USER_FILTER = async (DATA: UserFilterParams): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.USER_USERS}?status=${DATA.status}&page=${DATA.page}&limit=${DATA.limit}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error filtering user:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to filter user'));
  }
};

export const USER_SAVE = async (DATA: string, formData: FormData | Record<string, unknown>): Promise<ApiResponse> => {
  try {
    const response = await apiClient.put<ApiResponse>(`${ENDPOINTS.ADMIN.EDIT_USER_USERS}/${DATA}`, formData);
    return response.data;
  } catch (error: unknown) {
    console.error('Error saving user:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to save user'));
  }
};

export const DELETE_USER = async (DATA: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.delete<ApiResponse>(`${ENDPOINTS.ADMIN.EDIT_USER_USERS}/${DATA}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error deleting user:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to delete user'));
  }
};

export const EXPORT_USER = async (_DATA?: Record<string, unknown>): Promise<ApiResponse> => {
  try {
    const response = await apiClient.post<ApiResponse>(`${ENDPOINTS.ADMIN.USER_EXPORT}`, { format: 'csv' });
    return response.data;
  } catch (error: unknown) {
    console.error('Error exporting user:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to export user'));
  }
};

export const HOTSPOT_STATICTICS = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.HOTSPOT_STATISTICS}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching hotspot statistics:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch hotspot statistics'));
  }
};

export const HOTSPOTS_HOTSPOTS = async (DATA: HotspotListParams, data1?: Record<string, unknown>): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.HOTSPOTS_HOTSPOTS}?page=${DATA.page}&limit=${DATA.limit}`, data1);
    return response.data;
  } catch (error: unknown) {
    console.error('Error filtering hotspots:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to filter hotspots'));
  }
};

export const HOTSPOTS_LOCATION = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.HOTSPOT_LOCATION}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching hotspots location:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch hotspots location'));
  }
};

export const HOTSPOTS_SEARCH = async (DATA: HotspotSearchParams): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.HOTSPOTS_HOTSPOTS}?search=${DATA.search}&page=${DATA.page}&limit=${DATA.limit}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error searching hotspots:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to search hotspots'));
  }
};

export const HOTSPOTS_GRAPH = async (DATA: GraphParams): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.REFERRAL_GRAPH}?period=${DATA.period}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching hotspots graph:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch hotspots graph'));
  }
};

export const REFERRAL_STATS = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.REFERRAL_STATS}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching referral stats:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch referral stats'));
  }
};

export const REFERRAL_HOTSPOT = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.REFERRAL_HOTSPOT}?status=all&page=1&limit=10`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching referral hotspot:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch referral hotspot'));
  }
};

export const REFERRAL_GRAPH = async (DATA: GraphParams): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.REFERRAL_GRAPH}?period=${DATA.period}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching referral graph:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch referral graph'));
  }
};

export const PAYOUT_USERS = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.PAYOUT_USERS}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching payout users:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch payout users'));
  }
};

export const PAYOUT_GRAPH = async (DATA: GraphParams): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.PAYOUT_GRAPH}?period=${DATA.period}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching payout graph:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch payout graph'));
  }
};

export const SUSPEND_USER = async (userId: string, reason = 'Violation of policy'): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(ENDPOINTS.ADMIN.SUSPEND_USER(userId), { reason });
    return response.data;
  } catch (error: unknown) {
    console.error('Error suspending user:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to suspend user'));
  }
};

export const UNSUSPEND_USER = async (userId: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(ENDPOINTS.ADMIN.UNSUSPEND_USER(userId), {});
    return response.data;
  } catch (error: unknown) {
    console.error('Error unsuspending user:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to unsuspend user'));
  }
};

export const UPDATE_USER_WITHDRAWAL_ACCESS = async (
  userId: string,
  payload: { blocked: boolean; reason?: string }
): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(ENDPOINTS.ADMIN.WITHDRAWAL_ACCESS(userId), payload);
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating withdrawal access:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to update withdrawal access'));
  }
};

export const ADMIN_ORGANIZATIONS = async (DATA: { q?: string; page?: number; limit?: number }): Promise<ApiResponse> => {
  try {
    const params = new URLSearchParams();
    if (DATA?.q) params.append('q', DATA.q);
    params.append('page', String(DATA?.page || 1));
    params.append('limit', String(DATA?.limit || 10));

    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.ORGANIZATIONS}?${params.toString()}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching organizations:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch organizations'));
  }
};

export const REJECT_ORGANIZATION = async (organizationId: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(
      ENDPOINTS.ADMIN.REJECT_ORGANIZATION(organizationId),
      {}
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error rejecting organization:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to reject organization'));
  }
};

export const ADMIN_ORGANIZATION_REQUESTS = async (
  DATA: { status?: string; page?: number; limit?: number }
): Promise<ApiResponse> => {
  try {
    const params = new URLSearchParams();
    if (DATA?.status) params.append('status', DATA.status);
    params.append('page', String(DATA?.page || 1));
    params.append('limit', String(DATA?.limit || 10));

    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.ORGANIZATION_REQUESTS}?${params.toString()}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching organization requests:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch organization requests'));
  }
};

export const REJECT_ORGANIZATION_REQUEST = async (requestId: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(
      ENDPOINTS.ADMIN.REJECT_ORGANIZATION_REQUEST(requestId),
      {}
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error rejecting organization request:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to reject organization request'));
  }
};

export const ADMIN_COUNTRY_MANAGERS = async (
  DATA: { search?: string; status?: string; country?: string; page?: number; limit?: number }
): Promise<ApiResponse> => {
  const endpoints = [
    ENDPOINTS.ADMIN.COUNTRY_MANAGERS,
    '/admin/countryManagers',
  ];

  try {
    const params = new URLSearchParams();
    if (DATA?.search) params.append('search', DATA.search);
    if (DATA?.status) params.append('status', DATA.status);
    if (DATA?.country) params.append('country', DATA.country);
    params.append('page', String(DATA?.page || 1));
    params.append('limit', String(DATA?.limit || 10));
    let lastError: unknown = null;

    for (const endpoint of endpoints) {
      try {
        const response = await apiClient.get<ApiResponse>(`${endpoint}?${params.toString()}`);
        return response.data;
      } catch (error: unknown) {
        lastError = error;
      }
    }

    throw lastError;
  } catch (error: unknown) {
    console.error('Error fetching country managers:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch country managers'));
  }
};

export const REJECT_COUNTRY_MANAGER = async (countryManagerId: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.delete<ApiResponse>(
      ENDPOINTS.ADMIN.REJECT_COUNTRY_MANAGER(countryManagerId)
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error rejecting country manager:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to reject country manager'));
  }
};

export const ADMIN_COUNTRY_MANAGER_REQUESTS = async (
  DATA: CountryManagerRequestParams
): Promise<ApiResponse> => {
  const endpoints = [
    ENDPOINTS.ADMIN.COUNTRY_MANAGER_REQUESTS,
    '/admin/countryManagers/requests',
  ];

  try {
    const params = new URLSearchParams();
    if (DATA?.status) params.append('status', DATA.status);
    if (DATA?.search) params.append('search', DATA.search);
    if (DATA?.country) params.append('country', DATA.country);
    params.append('page', String(DATA?.page || 1));
    params.append('limit', String(DATA?.limit || 10));
    let lastError: unknown = null;

    for (const endpoint of endpoints) {
      try {
        const response = await apiClient.get<ApiResponse>(`${endpoint}?${params.toString()}`);
        return response.data;
      } catch (error: unknown) {
        lastError = error;
      }
    }

    throw lastError;
  } catch (error: unknown) {
    console.error('Error fetching country manager requests:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch country manager requests'));
  }
};

export const APPROVE_COUNTRY_MANAGER_REQUEST = async (requestId: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(
      ENDPOINTS.ADMIN.APPROVE_COUNTRY_MANAGER_REQUEST(requestId),
      {}
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error approving country manager request:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to approve country manager request'));
  }
};

export const REJECT_COUNTRY_MANAGER_REQUEST = async (requestId: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.delete<ApiResponse>(
      ENDPOINTS.ADMIN.REJECT_COUNTRY_MANAGER_REQUEST(requestId),
      {}
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error rejecting country manager request:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to reject country manager request'));
  }
};

// ─── KXT Admin Functions ──────────────────────────────────────────────────────

export const KXT_STATS = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.KXT_STATS}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching KXT stats:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch KXT stats'));
  }
};

export const KXT_POOL = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.KXT_POOL}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching KXT pool:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch KXT pool'));
  }
};

export const KXT_ADMIN_LEADERBOARD = async (period = 'weekly'): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.KXT_LEADERBOARD}?period=${period}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching KXT leaderboard:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch KXT leaderboard'));
  }
};

export const KXT_CONTRACT_STATUS = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(`${ENDPOINTS.ADMIN.KXT_CONTRACT_STATUS}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching contract status:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch contract status'));
  }
};

export const KXT_CONTRACT_PAUSE = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.post<ApiResponse>(`${ENDPOINTS.ADMIN.KXT_CONTRACT_PAUSE}`, {});
    return response.data;
  } catch (error: unknown) {
    console.error('Error pausing contract:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to pause contract'));
  }
};

export const KXT_CONTRACT_UNPAUSE = async (): Promise<ApiResponse> => {
  try {
    const response = await apiClient.post<ApiResponse>(`${ENDPOINTS.ADMIN.KXT_CONTRACT_UNPAUSE}`, {});
    return response.data;
  } catch (error: unknown) {
    console.error('Error unpausing contract:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to unpause contract'));
  }
};

export const KXT_RATE_USD = async (DATA: { kxt: number; usd: number }): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(ENDPOINTS.ADMIN.KXT_RATE_USD, DATA);
    return response.data;
  } catch (error: unknown) {
    console.error('Error saving KXT/USD rate:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to save KXT/USD rate'));
  }
};

export const GET_KXT_RATE_USD = async (): Promise<{ kxt: number; usd: number; rate: number }> => {
  try {
    const response = await apiClient.get<ApiResponse>(ENDPOINTS.ADMIN.KXT_RATE_USD);
    const payload = response.data?.data ?? response.data ?? {};
    const raw = payload as unknown as Record<string, unknown>;
    const kxt = Number(
      raw.kxt ??
      raw.baseKxt ??
      raw.kxtAmount ??
      raw.fromAmount ??
      raw.from ??
      1
    );
    const usd = Number(
      raw.usd ??
      raw.baseUsd ??
      raw.usdAmount ??
      raw.toAmount ??
      raw.to ??
      raw.valueUsd ??
      raw.rateUsd
    );
    const rate = Number(
      raw.rate ??
      raw.exchangeRate ??
      raw.kxtToUsdRate ??
      (Number.isFinite(kxt) && kxt > 0 && Number.isFinite(usd) ? usd / kxt : NaN)
    );

    if (!Number.isFinite(rate) || rate <= 0) {
      throw new Error('Invalid KXT/USD rate response');
    }

    return {
      kxt: Number.isFinite(kxt) && kxt > 0 ? kxt : 1,
      usd: Number.isFinite(usd) ? usd : rate,
      rate,
    };
  } catch (error: unknown) {
    console.error('Error fetching KXT/USD rate:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch KXT/USD rate'));
  }
};

interface KxtWithdrawalPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface NormalizedKxtWithdrawalsResponse {
  success?: boolean;
  message?: string;
  data: {
    withdrawals: unknown[];
    pagination: KxtWithdrawalPagination;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const normalizeKxtWithdrawalsResponse = (
  payload: Record<string, unknown>,
  DATA?: { page?: number; limit?: number; status?: string }
): NormalizedKxtWithdrawalsResponse => {
  const data = (payload.data || payload || {}) as unknown as Record<string, unknown>;
  const withdrawals = (
    data.withdrawals ||
    data.approvedWithdrawals ||
    data.rejectedWithdrawals ||
    payload.withdrawals ||
    payload.approvedWithdrawals ||
    payload.rejectedWithdrawals ||
    []
  ) as unknown[];
  const paginationPayload = (data.pagination || payload.pagination || {}) as unknown as Record<string, unknown>;
  const currentPage = Number(paginationPayload.currentPage || paginationPayload.page || DATA?.page || 1);
  const itemsPerPage = Number(paginationPayload.itemsPerPage || paginationPayload.limit || DATA?.limit || withdrawals.length || 10);
  const totalItems = Number(paginationPayload.totalItems || paginationPayload.total || withdrawals.length || 0);
  const totalPages = Number(
    paginationPayload.totalPages ||
    paginationPayload.pages ||
    Math.max(1, Math.ceil(totalItems / Math.max(itemsPerPage, 1)))
  );

  return {
    ...payload,
    data: {
      ...data,
      withdrawals,
      pagination: {
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        hasNext: (paginationPayload.hasNext ?? paginationPayload.hasMore ?? currentPage < totalPages) as boolean,
        hasPrevious: (paginationPayload.hasPrevious ?? currentPage > 1) as boolean,
      },
    },
  };
};

const fetchKxtWithdrawalCollection = async (
  endpoint: string,
  DATA?: { page?: number; limit?: number; status?: string }
): Promise<NormalizedKxtWithdrawalsResponse> => {
  try {
    const params = new URLSearchParams();
    if (DATA?.page) params.append('page', String(DATA.page));
    if (DATA?.limit) params.append('limit', String(DATA.limit));
    if (DATA?.status && DATA.status !== 'All') params.append('status', DATA.status);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<ApiResponse>(`${endpoint}${query}`);
    return normalizeKxtWithdrawalsResponse((response.data || {}) as unknown as Record<string, unknown>, DATA);
  } catch (error: unknown) {
    console.error('Error fetching KXT withdrawals:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch KXT withdrawals'));
  }
};

export const KXT_WITHDRAWALS = async (DATA?: { page?: number; limit?: number; status?: string }): Promise<NormalizedKxtWithdrawalsResponse> =>
  fetchKxtWithdrawalCollection(ENDPOINTS.ADMIN.KXT_WITHDRAWALS, DATA);

export const KXT_WITHDRAWALS_APPROVED = async (DATA?: { page?: number; limit?: number }): Promise<NormalizedKxtWithdrawalsResponse> =>
  fetchKxtWithdrawalCollection(ENDPOINTS.ADMIN.KXT_WITHDRAWALS_APPROVED, DATA);

export const KXT_WITHDRAWALS_REJECTED = async (DATA?: { page?: number; limit?: number }): Promise<NormalizedKxtWithdrawalsResponse> =>
  fetchKxtWithdrawalCollection(ENDPOINTS.ADMIN.KXT_WITHDRAWALS_REJECTED, DATA);

export const KXT_WITHDRAWAL_BY_ID = async (id: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.get<ApiResponse>(ENDPOINTS.ADMIN.KXT_WITHDRAWAL_BY_ID(id));
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching withdrawal details:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to fetch withdrawal details'));
  }
};

export const KXT_APPROVE = async (id: string, payload?: Record<string, unknown>): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(ENDPOINTS.ADMIN.KXT_APPROVE(id), payload ?? {});
    return response.data;
  } catch (error: unknown) {
    console.error('Error approving KXT withdrawal:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to approve withdrawal'));
  }
};

export const KXT_REJECT = async (id: string, reason?: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.patch<ApiResponse>(ENDPOINTS.ADMIN.KXT_REJECT(id), { reason: reason || 'Rejected by admin' });
    return response.data;
  } catch (error: unknown) {
    console.error('Error rejecting KXT withdrawal:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to reject withdrawal'));
  }
};

export const KXT_MINT = async (DATA: { operatorId: string; amount: number; reason?: string }): Promise<ApiResponse> => {
  try {
    const response = await apiClient.post<ApiResponse>(`${ENDPOINTS.ADMIN.KXT_MINT}`, DATA);
    return response.data;
  } catch (error: unknown) {
    console.error('Error minting KXT:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to mint KXT'));
  }
};

export const CREATE_PLAN = async (DATA: PlanData): Promise<ApiResponse> => {
  try {
    const response = await apiClient.post<ApiResponse>(`${ENDPOINTS.ADMIN.PLAN_CREATE}`, DATA);
    return response.data;
  } catch (error: unknown) {
    console.error('Error creating plan:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to create plan'));
  }
};

export const EDIT_PLAN = async (planId: string, DATA: PlanData): Promise<ApiResponse> => {
  try {
    const response = await apiClient.put<ApiResponse>(`${ENDPOINTS.ADMIN.PLAN_EDIT(planId)}`, DATA);
    return response.data;
  } catch (error: unknown) {
    console.error('Error editing plan:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to edit plan'));
  }
};

export const DELETE_PLAN = async (planId: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.delete<ApiResponse>(`${ENDPOINTS.ADMIN.PLAN_DELETE(planId)}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error deleting plan:', error);
    throw new Error(getAdminErrorMessage(error, 'Failed to delete plan'));
  }
};

export default {
  DASHBOARD,
  CREATE_PLAN,
  EDIT_PLAN,
  DELETE_PLAN,
  USERS,
  GROWTH,
  HOTSPOT,
  USER_STATISTICS,
  USER_USERS,
  USER_SAVE,
  SUSPEND_USER,
  UNSUSPEND_USER,
  UPDATE_USER_WITHDRAWAL_ACCESS,
  USER_SEARCH,
  USER_FILTER,
  USER_DELETE,
  USER_EDIT,
  HOTSPOT_STATICTICS,
  HOTSPOTS_HOTSPOTS,
  HOTSPOTS_SEARCH,
  EXPORT_USER,
  HOTSPOTS_GRAPH,
  HOTSPOTS_LOCATION,
  REFERRAL_STATS,
  REFERRAL_HOTSPOT,
  REFERRAL_GRAPH,
  PAYOUT_USERS,
  PAYOUT_GRAPH,
  ADMIN_ORGANIZATIONS,
  REJECT_ORGANIZATION,
  ADMIN_ORGANIZATION_REQUESTS,
  REJECT_ORGANIZATION_REQUEST,
  ADMIN_COUNTRY_MANAGERS,
  REJECT_COUNTRY_MANAGER,
  ADMIN_COUNTRY_MANAGER_REQUESTS,
  APPROVE_COUNTRY_MANAGER_REQUEST,
  REJECT_COUNTRY_MANAGER_REQUEST,
  KXT_STATS,
  KXT_POOL,
  KXT_ADMIN_LEADERBOARD,
  KXT_CONTRACT_STATUS,
  KXT_CONTRACT_PAUSE,
  KXT_CONTRACT_UNPAUSE,
  KXT_WITHDRAWALS,
  KXT_WITHDRAWAL_BY_ID,
  KXT_APPROVE,
  KXT_REJECT,
  KXT_MINT,
};
